import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAgency } from '@/contexts/AgencyContext';

export type ProductUpdateType = 'feature' | 'fix' | 'improvement' | 'announcement';

export interface ProductUpdate {
  id: string;
  version: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  type: ProductUpdateType;
  priority: number;
  target_roles: string[];
  pinned: boolean;
  critical: boolean;
  dismissible: boolean;
  published: boolean;
  published_at: string | null;
  hero_image: string | null;
  cta_label: string | null;
  cta_url: string | null;
  created_at: string;
}

export type DismissAction = 'read' | 'dismissed' | 'remind_later' | 'never_show';

export interface UserUpdateDismissal {
  id: string;
  user_id: string;
  update_id: string;
  action: DismissAction;
  remind_after: string | null;
  updated_at: string;
}

/** Map agency role → audience role token used in product_updates.target_roles. */
function audienceRoleFor(agencyRole: string | null, isPlatformAdmin: boolean): string[] {
  const roles: string[] = ['all'];
  if (isPlatformAdmin) roles.push('platform_admin');
  if (agencyRole === 'owner') roles.push('agency_owner');
  if (agencyRole === 'admin') roles.push('agency_admin');
  if (agencyRole === 'manager') roles.push('agency_manager');
  if (agencyRole === 'staff') roles.push('agency_staff');
  return roles;
}

export function useProductUpdates() {
  const { user } = useAuth();
  const { agencyRole, isPlatformSuperAdmin } = useAgency();
  const qc = useQueryClient();

  const audience = useMemo(
    () => audienceRoleFor(agencyRole, isPlatformSuperAdmin),
    [agencyRole, isPlatformSuperAdmin],
  );

  const updatesQ = useQuery({
    queryKey: ['product-updates'],
    queryFn: async (): Promise<ProductUpdate[]> => {
      const { data, error } = await supabase
        .from('product_updates' as any)
        .select('*')
        .eq('published', true)
        .order('pinned', { ascending: false })
        .order('priority', { ascending: false })
        .order('published_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProductUpdate[];
    },
  });

  const dismissalsQ = useQuery({
    queryKey: ['product-updates-dismissals', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<UserUpdateDismissal[]> => {
      const { data, error } = await supabase
        .from('user_update_dismissals' as any)
        .select('*')
        .eq('user_id', user!.id);
      if (error) throw error;
      return (data ?? []) as unknown as UserUpdateDismissal[];
    },
  });

  const allUpdates = useMemo(() => {
    const list = updatesQ.data ?? [];
    return list.filter((u) =>
      !u.target_roles?.length ||
      u.target_roles.some((r) => audience.includes(r)),
    );
  }, [updatesQ.data, audience]);

  const dismissalMap = useMemo(() => {
    const m = new Map<string, UserUpdateDismissal>();
    for (const d of dismissalsQ.data ?? []) m.set(d.update_id, d);
    return m;
  }, [dismissalsQ.data]);

  const now = Date.now();

  /** Updates that should still display in the modal (not "never", not currently snoozed, not read). */
  const pendingForModal = useMemo(() => {
    return allUpdates.filter((u) => {
      const d = dismissalMap.get(u.id);
      if (!d) return true;
      if (d.action === 'never_show' || d.action === 'read' || d.action === 'dismissed') return false;
      if (d.action === 'remind_later') {
        return !d.remind_after || new Date(d.remind_after).getTime() <= now;
      }
      return true;
    });
  }, [allUpdates, dismissalMap, now]);

  const unreadCount = useMemo(() => {
    return allUpdates.filter((u) => {
      const d = dismissalMap.get(u.id);
      if (!d) return true;
      return d.action !== 'read' && d.action !== 'never_show';
    }).length;
  }, [allUpdates, dismissalMap]);

  const setDismissal = useMutation({
    mutationFn: async ({ updateId, action, remindAfter }: { updateId: string; action: DismissAction; remindAfter?: string | null }) => {
      if (!user?.id) throw new Error('Not signed in');
      const { error } = await supabase
        .from('user_update_dismissals' as any)
        .upsert(
          {
            user_id: user.id,
            update_id: updateId,
            action,
            remind_after: remindAfter ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,update_id' },
        );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-updates-dismissals', user?.id] }),
  });

  return {
    updates: allUpdates,
    dismissalMap,
    pendingForModal,
    unreadCount,
    isLoading: updatesQ.isLoading || dismissalsQ.isLoading,
    setDismissal: setDismissal.mutateAsync,
  };
}
